import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  offeringCalendarDisabledDates,
  offeringCalendarMarkedDates,
  type Gathering,
} from '@/components/programs/gathering-schedule';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { api } from '@/lib/api';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';

interface ContributionGatheringFieldsProps {
  open: boolean;
  programId: string | null;
  serviceDate: string | null;
  onProgramChange: (programId: string | null) => void;
  onServiceDateChange: (serviceDate: string | null) => void;
}

export const ContributionGatheringFields = ({
  open,
  programId,
  serviceDate,
  onProgramChange,
  onServiceDateChange,
}: ContributionGatheringFieldsProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const church = useChurchStore((state) => state.church);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';

  const programs = useQuery({
    queryKey: ['programs', 'contribution-form'],
    queryFn: () => api.get<{ data: Gathering[] }>('/programs?pageSize=100'),
    enabled: open,
  });

  const today = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');
  const selected = programs.data?.data.find((row) => row.id === programId) ?? null;

  const markedDates = useMemo(
    () => (selected ? offeringCalendarMarkedDates(selected, today) : []),
    [selected, today],
  );

  const disabledDates = useMemo(() => {
    if (!selected || markedDates.length === 0) return [];
    const past =
      DateTime.fromISO(today, { zone: timezone }).minus({ days: 366 }).toFormat('yyyy-MM-dd') ??
      today;
    const future =
      DateTime.fromISO(today, { zone: timezone }).plus({ days: 366 }).toFormat('yyyy-MM-dd') ??
      today;
    return offeringCalendarDisabledDates(markedDates, past, future);
  }, [markedDates, selected, timezone, today]);

  const options = [...(programs.data?.data ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((row) => ({
      value: row.id,
      label: row.kind === 'program' ? `${row.name} (${t('form.programKind')})` : row.name,
    }));

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label>
          {t('form.gathering')}{' '}
          <span className="text-fg-secondary">({t('form.memberOptional')})</span>
        </Label>
        <Listbox
          value={programId}
          onChange={(next) => {
            onProgramChange(next);
            onServiceDateChange(null);
          }}
          options={options}
          placeholder={t('form.gatheringPlaceholder')}
          clearable
          clearLabel={tCommon('action.clearSelection')}
          popoverPortal="dialog"
        />
      </div>
      {programId ? (
        <div className="flex flex-col gap-1.5">
          <Label>{t('form.gatheringDate')}</Label>
          <DatePicker
            value={serviceDate}
            onChange={onServiceDateChange}
            disabledDates={disabledDates}
            markedDates={markedDates}
            popoverPortal="dialog"
          />
          <p className="text-caption text-fg-secondary">{t('form.gatheringDateHint')}</p>
        </div>
      ) : null}
    </>
  );
};
